import { type ComponentProps, memo, type ReactNode, useCallback, useEffect, useState } from 'react';
import invariant from 'tiny-invariant';

import { XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const banner = tailwindVariants({
  defaultVariants: {
    color: 'yellow',
  },
  slots: {
    base: 'relative flex min-h-[44px] shrink-0 items-start gap-2 rounded-12 border px-4 py-3 text-left ads:text-primary text-gray-12',
    content: 'relative z-1 min-w-0 flex-1 text-14 leading-5',
    prefix: 'flex h-5 shrink-0 items-center',
  },
  variants: {
    // Content sits above the click overlay; let body clicks fall through to it while inline controls stay clickable.
    clickable: {
      true: { content: 'pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto' },
    },
    color: {
      airFlash: {
        base: 'ads:border-error border-flash-6 ads:bg-error-subtle bg-flash-3',
        prefix: 'ads:text-error text-flash-11',
      },
      amber: {
        base: 'ads:border-warning border-amber-6 ads:bg-warning bg-amber-3',
        prefix: 'ads:text-warning text-amber-9',
      },
      blue: {
        base: 'ads:border-primary-subtle border-altitude-7 ads:bg-primary-subtle bg-altitude-3',
        prefix: 'ads:text-icon-primary text-altitude-10',
      },
      cyan: {
        base: 'ads:border-ads-cyan-6 border-cyan-6 ads:bg-promotional bg-cyan-3',
        prefix: 'ads:text-primary text-cyan-10',
      },
      grey: {
        base: 'ads:border-default border-gray-4 ads:bg-subtle bg-gray-3',
        prefix: 'ads:text-icon-default text-gray-10',
      },
      red: {
        base: 'ads:border-error border-flash-6 ads:bg-error-subtle bg-flash-3',
        prefix: 'ads:text-error text-flash-11',
      },
      teal: {
        base: 'ads:border-ads-cyan-6 border-cyan-6 ads:bg-promotional bg-cyan-3',
        prefix: 'ads:text-primary text-cyan-10',
      },
      violet: {
        base: 'ads:border-ads-violet-6 border-violet-6 ads:bg-ads-violet-3 bg-violet-3',
        prefix: 'ads:text-ads-violet-10 text-violet-10',
      },
      yellow: {
        base: 'ads:border-warning border-amber-6 ads:bg-warning bg-amber-3',
        prefix: 'ads:text-warning text-amber-9',
      },
    },
  },
});

export type AlertBannerVariants = VariantProps<typeof banner>;

export type AlertBannerProps = Omit<ComponentProps<'div'>, 'onClick' | 'prefix'> &
  Pick<ComponentProps<'button'>, 'onClick'> &
  Omit<AlertBannerVariants, 'clickable'> & {
    defaultState?: boolean;
    /**
     * Number of days to dismiss the banner for if the user
     * clicks the dismiss button.
     */
    dismissDays?: number;
    id?: string;
    onDismiss?: () => void;
    prefix?: ReactNode;
  };

export const AlertBanner = memo(
  ({
    children,
    className,
    color,
    dismissDays,
    defaultState = true,
    id,
    onClick,
    onDismiss,
    prefix,
    ...restOfProps
  }: AlertBannerProps) => {
    const [isVisible, setIsVisible] = useState(defaultState);

    invariant(id || !dismissDays, 'If dismissDays is provided, an id must also be provided.');
    invariant(dismissDays || !id, 'If an id is provided, dismissDays must also be provided.');

    useEffect(() => {
      /**
       * If the banner has a dismissDays prop, check localStorage to
       * see if the banner should be visible or not.
       */
      if (dismissDays && id) {
        const alertBannerStorageValue = localStorage.getItem(id);

        if (alertBannerStorageValue) {
          const alertBannerStorageDate = new Date(alertBannerStorageValue);
          const alertBannerStorageDatePlusDays = new Date(
            alertBannerStorageDate.setDate(alertBannerStorageDate.getDate() + dismissDays),
          );

          if (alertBannerStorageDatePlusDays > new Date()) {
            setIsVisible(false);
          } else {
            setIsVisible(true);
          }
        }
      }
    }, [dismissDays, id]);

    const handleDismiss = useCallback(() => {
      setIsVisible(false);

      if (dismissDays && id) {
        localStorage.setItem(id, new Date().toISOString());
      }

      onDismiss?.();
    }, [dismissDays, id, onDismiss]);

    if (!isVisible) {
      return null;
    }

    const styles = banner({ clickable: !!onClick, color });

    return (
      <div className={styles.base({ className })} data-testid="ALERT_BANNER" role="alert" {...restOfProps}>
        {onClick && (
          <button
            className="absolute inset-0 size-full cursor-pointer rounded-[inherit] border-0 bg-transparent outline-none focus-visible:ads:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9"
            onClick={onClick}
            type="button"
          />
        )}

        {!!prefix && <div className={styles.prefix()}>{prefix}</div>}

        <div className={styles.content()}>{children}</div>

        {!!(onDismiss || dismissDays) && (
          <div className="relative z-1 -my-0.5 flex shrink-0 items-center">
            <IconButton
              appearance="ghost"
              color="gray"
              data-testid="ALERT_BANNER_CLOSE"
              label="Close banner"
              onClick={handleDismiss}
              size="small"
            >
              <XIcon size="xs" />
            </IconButton>
          </div>
        )}
      </div>
    );
  },
);

AlertBanner.displayName = 'AlertBanner';
